import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import OEContext from "../../layouts/OEContext";
import { axiosGet, axiosPost } from "../../data/helpers/baseApi";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import LoadingIndicator from "../../components/LoadingIndicator";
import PlanContributionMultiSelect from "./PlanContributionMultiSelect";
import FlexContributionLimitLabel from "../../components/FlexContributionLimitLabel";
import { useQueryGetFlexContributionLimits } from "../../data/services/flexBenefitsService";

const yesNoOptions = ["Yes", "No"];
const contributionScheduleOptions = [
  "Per pay period",
  "Monthly on the first payroll of the month",
  "Quarterly on the first pay period of the quarter",
  "Bi-yearly on the first payroll of the period (first in January and first in July)",
  "Yearly on the first pay period of the year",
];
const contributionPayPeriodOptions = ["First Pay Period", "Second Pay Period"];
const matchOrContributionOptions = ["Match", "Contribution"];
const currentPlanYear = `${new Date().getFullYear() + 1}`;

const normalizeOption = (value, allowedOptions) => {
  if (value === null || value === undefined) {
    return "";
  }

  const normalizedValue = `${value}`.trim();
  if (!normalizedValue) {
    return "";
  }

  return (
    allowedOptions.find(
      (option) => option.toLowerCase() === normalizedValue.toLowerCase(),
    ) ?? ""
  );
};

const initialFormData = {
  enrollmentYear: currentPlanYear,
  hasCurrentHsaVendor: "",
  joinPrestigeHsaPlan: "",
  employerContributionOffered: "",
  contributionSchedule: "",
  contributionPayPeriod: "",
  matchOrContribution: "",
  plansAvailableForContribution: "",
  benefitGroupsEligibleForContribution: "",
  annualEEContribution: "",
  annualESContribution: "",
  annualECContribution: "",
  annualFAMContribution: "",
  prorates: "",
  notes: "",
};

const createNoDataFormData = () => ({
  ...initialFormData,
  enrollmentYear: currentPlanYear,
});

const formatOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return `${value}`;
};

const hydrateFormData = (data) => ({
  enrollmentYear: currentPlanYear,
  hasCurrentHsaVendor: normalizeOption(data?.hasCurrentHsaVendor, yesNoOptions),
  joinPrestigeHsaPlan: normalizeOption(data?.joinPrestigeHsaPlan, yesNoOptions),
  employerContributionOffered: normalizeOption(
    data?.employerContributionOffered,
    yesNoOptions,
  ),
  contributionSchedule: normalizeOption(
    data?.contributionSchedule,
    contributionScheduleOptions,
  ),
  contributionPayPeriod: normalizeOption(
    data?.contributionPayPeriod,
    contributionPayPeriodOptions,
  ),
  matchOrContribution: normalizeOption(
    data?.matchOrContribution,
    matchOrContributionOptions,
  ),
  plansAvailableForContribution: data?.plansAvailableForContribution ?? "",
  benefitGroupsEligibleForContribution:
    data?.benefitGroupsEligibleForContribution ?? "",
  annualEEContribution: formatOptionalNumber(data?.annualEEContribution),
  annualESContribution: formatOptionalNumber(data?.annualESContribution),
  annualECContribution: formatOptionalNumber(data?.annualECContribution),
  annualFAMContribution: formatOptionalNumber(data?.annualFAMContribution),
  prorates: normalizeOption(data?.prorates, yesNoOptions),
  notes: data?.notes ?? "",
});

const parseOptionalNumber = (value) => {
  if (value === "") {
    return null;
  }

  return Number(value);
};

const pageStyle = {
  width: "100%",
  padding: "0 0 2rem",
  boxSizing: "border-box",
};
const contentStyle = {
  width: "100%",
  margin: 0,
  fontFamily: '"Segoe UI", Arial, sans-serif',
};
const formStyle = {
  width: "100%",
  padding: "20px 24px 24px",
  borderRadius: 0,
  border: "1px solid #d9d9d9",
  backgroundColor: "#fff",
  boxSizing: "border-box",
};
const sectionHeaderStyle = {
  marginBottom: 20,
};
const sectionTitleStyle = {
  margin: 0,
  fontSize: 24,
  color: "#333",
};
const sectionSubtitleStyle = {
  margin: "0.4rem 0 0",
  fontSize: 14,
  color: "#666",
};
const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 20,
  alignItems: "start",
};
const sectionStyle = {
  width: "100%",
  padding: 0,
  boxSizing: "border-box",
  textAlign: "left",
};
const fullWidthSectionStyle = {
  ...sectionStyle,
  gridColumn: "1 / -1",
};
const contributionGroupStyle = {
  gridColumn: "1 / -1",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 20,
  padding: "18px 0 0",
  borderTop: "1px solid #e5e5e5",
  boxSizing: "border-box",
};
const contributionHeaderStyle = {
  gridColumn: "1 / -1",
};
const contributionTitleStyle = {
  margin: 0,
  fontSize: 18,
  color: "#333",
};
const contributionTextStyle = {
  margin: "0.4rem 0 0",
  fontSize: 14,
  color: "#666",
};
const labelStyle = {
  display: "block",
  fontWeight: 700,
  marginBottom: 8,
  color: "#333",
};
const controlStyle = {
  width: "100%",
  minHeight: 46,
  padding: "10px 12px",
  fontSize: 15,
  borderRadius: 4,
  border: "1px solid #c8c8c8",
  backgroundColor: "#fff",
  boxSizing: "border-box",
};
const notesStyle = {
  ...controlStyle,
  minHeight: 120,
  resize: "vertical",
};
const errorStyle = {
  marginBottom: 20,
  padding: 14,
  borderRadius: 4,
  border: "1px solid #d14343",
  backgroundColor: "#fff1f1",
  color: "#7a1010",
};
const successStyle = {
  marginBottom: 20,
  padding: 14,
  borderRadius: 4,
  border: "1px solid #2d8a39",
  backgroundColor: "#edf8ee",
  color: "#1f6f2b",
};
const buttonRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 24,
};
const primaryButtonStyle = {
  backgroundColor: "#0078d4",
  color: "#fff",
  border: "none",
  padding: "12px 24px",
  fontSize: 15,
  fontWeight: 600,
  borderRadius: 4,
  cursor: "pointer",
};

const EmployerHSAForm = ({
  clientId: clientIdProp,
  oePeriodId: oePeriodIdProp,
  hasHdMedicalPlanSelected = true,
  planOptions = [],
  benefitGroupOptions = [],
}) => {
  const [hsaLoading, setHsaLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const routeParams = useParams();
  const clientId = clientIdProp ?? routeParams.clientId;
  const oePeriod = React.useContext(OEContext);
  const effectiveOePeriodId =
    oePeriod?.oePeriodId ?? oePeriodIdProp ?? routeParams.oePeriodId;
  const { data: flexLimitsResponse } = useQueryGetFlexContributionLimits(
    effectiveOePeriodId,
  );
  const flexLimits = flexLimitsResponse?.data;
  const [formData, setFormData] = useState(initialFormData);
  const isSubmitDisabled = hsaLoading || !hasHdMedicalPlanSelected;

  useEffect(() => {
    let isMounted = true;

    const loadHsaData = async () => {
      if (!clientId || !effectiveOePeriodId) {
        setHsaLoading(false);
        setErrorMessage(
          "HSA context is missing. Please reload this page from the rate model flow.",
        );
        setFormData(initialFormData);
        return;
      }

      setHsaLoading(true);
      setSuccessMessage("");
      setErrorMessage("");

      try {
        const response = await axiosGet(
          SERVICE_URLS.CLIENT_HSA_MATCH(effectiveOePeriodId, clientId),
        );

        if (isMounted && response?.status === 200 && response.data) {
          setFormData(hydrateFormData(response.data));
        } else if (isMounted && response?.status === 204) {
          setFormData(createNoDataFormData());
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(
            "HSA details could not be loaded right now. You can still review the page, but HSA data is unavailable until the backend issue is resolved.",
          );
          setFormData(initialFormData);
        }
      } finally {
        if (isMounted) {
          setHsaLoading(false);
        }
      }
    };

    loadHsaData();

    return () => {
      isMounted = false;
    };
  }, [clientId, effectiveOePeriodId]);

  if (hsaLoading) {
    return <LoadingIndicator />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (successMessage) {
      setSuccessMessage("");
    }

    if (errorMessage) {
      setErrorMessage("");
    }

    setFormData((prev) => {
      const nextFormData = {
        ...prev,
        [name]: value,
      };

      if (
        !hasHdMedicalPlanSelected &&
        name === "joinPrestigeHsaPlan" &&
        value === "Yes"
      ) {
        nextFormData.joinPrestigeHsaPlan = "No";
      }

      if (name === "employerContributionOffered" && value !== "Yes") {
        nextFormData.contributionSchedule = "";
        nextFormData.contributionPayPeriod = "";
        nextFormData.matchOrContribution = "";
        nextFormData.plansAvailableForContribution = "";
        nextFormData.benefitGroupsEligibleForContribution = "";
        nextFormData.annualEEContribution = "";
        nextFormData.annualESContribution = "";
        nextFormData.annualECContribution = "";
        nextFormData.annualFAMContribution = "";
        nextFormData.prorates = "";
      }

      return nextFormData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasHdMedicalPlanSelected) {
      setErrorMessage(
        "HSA cannot be selected unless at least one HD medical plan is selected in the contribution model.",
      );
      return;
    }

    if (!clientId || !effectiveOePeriodId) {
      setErrorMessage(
        "HSA context is missing. Please reload this page from the rate model flow.",
      );
      return;
    }

    const numericOePeriodId = Number(effectiveOePeriodId);
    if (!Number.isFinite(numericOePeriodId)) {
      setErrorMessage(
        "OE period could not be determined. Please reload the page.",
      );
      return;
    }

    if (!hasHdMedicalPlanSelected && formData.joinPrestigeHsaPlan === "Yes") {
      setErrorMessage(
        "HSA can only be selected when at least one HD medical plan is selected.",
      );
      return;
    }

    setHsaLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const result = await axiosPost(
        SERVICE_URLS.CLIENT_HSA_MATCH(numericOePeriodId, clientId),
        {
          oePeriodId: numericOePeriodId,
          clientId,
          enrollmentYear: currentPlanYear,
          hasCurrentHsaVendor:
            normalizeOption(formData.hasCurrentHsaVendor, yesNoOptions) || null,
          joinPrestigeHsaPlan:
            normalizeOption(formData.joinPrestigeHsaPlan, yesNoOptions) || null,
          employerContributionOffered:
            normalizeOption(
              formData.employerContributionOffered,
              yesNoOptions,
            ) || null,
          contributionSchedule:
            formData.employerContributionOffered === "Yes"
              ? normalizeOption(
                  formData.contributionSchedule,
                  contributionScheduleOptions,
                ) || null
              : null,
          contributionPayPeriod:
            formData.employerContributionOffered === "Yes" &&
            formData.contributionPayPeriod
              ? normalizeOption(
                  formData.contributionPayPeriod,
                  contributionPayPeriodOptions,
                ) || null
              : null,
          matchOrContribution:
            formData.employerContributionOffered === "Yes"
              ? normalizeOption(
                  formData.matchOrContribution,
                  matchOrContributionOptions,
                ) || null
              : null,
          plansAvailableForContribution:
            formData.employerContributionOffered === "Yes"
              ? formData.plansAvailableForContribution || null
              : null,
          benefitGroupsEligibleForContribution:
            formData.employerContributionOffered === "Yes"
              ? formData.benefitGroupsEligibleForContribution || null
              : null,
          annualEEContribution:
            formData.employerContributionOffered === "Yes"
              ? parseOptionalNumber(formData.annualEEContribution)
              : null,
          annualESContribution:
            formData.employerContributionOffered === "Yes"
              ? parseOptionalNumber(formData.annualESContribution)
              : null,
          annualECContribution:
            formData.employerContributionOffered === "Yes"
              ? parseOptionalNumber(formData.annualECContribution)
              : null,
          annualFAMContribution:
            formData.employerContributionOffered === "Yes"
              ? parseOptionalNumber(formData.annualFAMContribution)
              : null,
          prorates:
            formData.employerContributionOffered === "Yes"
              ? normalizeOption(formData.prorates, yesNoOptions) || null
              : null,
          notes: formData.notes || null,
        },
      );

      if (result?.status === 200 || result?.status === 201) {
        let hydratedData = null;

        if (
          result?.data &&
          typeof result.data === "object" &&
          !Array.isArray(result.data)
        ) {
          hydratedData = result.data;
        } else {
          try {
            const refresh = await axiosGet(
              SERVICE_URLS.CLIENT_HSA_MATCH(numericOePeriodId, clientId),
            );

            if (refresh?.status === 200 && refresh.data) {
              hydratedData = refresh.data;
            }
          } catch {
            // Keep the current form state if re-fetch fails.
          }
        }

        if (hydratedData) {
          setFormData(hydrateFormData(hydratedData));
        }

        setSuccessMessage("HSA details were saved successfully.");
      } else {
        setErrorMessage(
          "We couldn't save the HSA details right now. Please review the information and try again.",
        );
      }
    } catch (error) {
      setErrorMessage(
        "We couldn't save the HSA details right now. Please review the information and try again.",
      );
    } finally {
      setHsaLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={contentStyle}>
        <div style={sectionHeaderStyle}>
          <div>
            <h2 style={sectionTitleStyle}>HSA - Health Savings Account</h2>
            <p style={sectionSubtitleStyle}>
              Enter the HSA setup details for this client. Additional
              contribution fields appear only when needed.
            </p>
            <div>
              <FlexContributionLimitLabel
                label="HSA (Individual)"
                amount={flexLimits?.hsaIndividual}
              />
              <FlexContributionLimitLabel
                label="HSA (Family)"
                amount={flexLimits?.hsaFamily}
              />
            </div>
            <div style={labelStyle}>HSA Contribution Awareness Notice</div>
            <p style={{ ...sectionSubtitleStyle, width: 620 }}>
              Employers may elect to offer and fund Health Savings Account (HSA) contributions
              as part of their benefits strategy. HSA participation, eligibility, and
              contribution limits are subject to applicable IRS regulations, eligibility
              requirements, and plan document provisions. Additional catch-up contribution
              opportunities may be available for eligible individuals age 55 and older, and
              certain circumstances, including spousal HSA eligibility, may impact allowable
              contribution amounts. Employer contributions count toward the applicable IRS
              maximum and should be considered when establishing an HSA funding strategy.
              In all cases, the applicable IRS regulations and plan documents will govern.
              If you have questions regarding HSA participation or contribution elections,
              please contact your dedicated PrestigePEO Benefits Specialist or Business Development Manager.
              This notice does not constitute tax, legal, or financial advice and emploers should consult a 
              qualified advisor regarding their individual circumstances.
            </p>
          </div>
        </div>

        {!hasHdMedicalPlanSelected ? (
          <div style={errorStyle}>
            HSA cannot be selected unless at least one HD medical plan is
            selected in the contribution model.
          </div>
        ) : null}

        <form onSubmit={handleSubmit} style={formStyle}>
          {successMessage ? (
            <div style={successStyle}>{successMessage}</div>
          ) : null}
          {errorMessage ? <div style={errorStyle}>{errorMessage}</div> : null}

          <div style={formGridStyle}>
            <div style={sectionStyle}>
              <label htmlFor="enrollmentYear" style={labelStyle}>
                HSA plan year
              </label>
              <input
                type="text"
                id="enrollmentYear"
                name="enrollmentYear"
                value={formData.enrollmentYear}
                onChange={handleChange}
                readOnly
                style={controlStyle}
              />
            </div>

            <div style={sectionStyle}>
              <label htmlFor="hasCurrentHsaVendor" style={labelStyle}>
                Company currently has an HSA vendor?
              </label>
              <select
                id="hasCurrentHsaVendor"
                name="hasCurrentHsaVendor"
                value={formData.hasCurrentHsaVendor}
                onChange={handleChange}
                required
                style={controlStyle}
              >
                <option value="">Select...</option>
                {yesNoOptions.map((option) => (
                  <option
                    key={option}
                    value={option}
                    disabled={!hasHdMedicalPlanSelected && option === "Yes"}
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div style={sectionStyle}>
              <label htmlFor="joinPrestigeHsaPlan" style={labelStyle}>
                Does company want to join Prestige's HSA plan?
              </label>
              <select
                id="joinPrestigeHsaPlan"
                name="joinPrestigeHsaPlan"
                value={formData.joinPrestigeHsaPlan}
                onChange={handleChange}
                required
                style={controlStyle}
              >
                <option value="">Select...</option>
                {yesNoOptions.map((option) => (
                  <option
                    key={option}
                    value={option}
                    disabled={!hasHdMedicalPlanSelected && option === "Yes"}
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div style={sectionStyle}>
              <label htmlFor="employerContributionOffered" style={labelStyle}>
                Does company contribute to the HSA?
              </label>
              <select
                id="employerContributionOffered"
                name="employerContributionOffered"
                value={formData.employerContributionOffered}
                onChange={handleChange}
                required
                style={controlStyle}
              >
                <option value="">Select...</option>
                {yesNoOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {formData.employerContributionOffered === "Yes" && (
              <div style={contributionGroupStyle}>
                <div style={contributionHeaderStyle}>
                  <h4 style={contributionTitleStyle}>
                    Employer contribution settings
                  </h4>
                  <p style={contributionTextStyle}>
                    These fields appear only when the company contributes to the
                    HSA.
                  </p>
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="contributionSchedule" style={labelStyle}>
                    HSA contribution schedule
                  </label>
                  <select
                    id="contributionSchedule"
                    name="contributionSchedule"
                    value={formData.contributionSchedule}
                    onChange={handleChange}
                    required
                    style={controlStyle}
                  >
                    <option value="">Select schedule...</option>
                    {contributionScheduleOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="contributionPayPeriod" style={labelStyle}>
                    What pay period?
                  </label>
                  <select
                    id="contributionPayPeriod"
                    name="contributionPayPeriod"
                    value={formData.contributionPayPeriod}
                    onChange={handleChange}
                    style={controlStyle}
                  >
                    <option value="">Select pay period...</option>
                    {contributionPayPeriodOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="matchOrContribution" style={labelStyle}>
                    Match or contribution?
                  </label>
                  <select
                    id="matchOrContribution"
                    name="matchOrContribution"
                    value={formData.matchOrContribution}
                    onChange={handleChange}
                    required
                    style={controlStyle}
                  >
                    <option value="">Select...</option>
                    {matchOrContributionOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={sectionStyle}>
                  <label
                    htmlFor="plansAvailableForContribution"
                    style={labelStyle}
                  >
                    Plans available for contribution
                  </label>
                  <PlanContributionMultiSelect
                    id="plansAvailableForContribution"
                    name="plansAvailableForContribution"
                    options={planOptions}
                    value={formData.plansAvailableForContribution}
                    onChange={handleChange}
                    disabled={planOptions.length === 0}
                    placeholder="Select high deductible plans..."
                    style={controlStyle}
                  />
                  {planOptions.length === 0 ? (
                    <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
                      No high deductible medical plans are available in this
                      model.
                    </div>
                  ) : null}
                </div>

                <div style={sectionStyle}>
                  <label
                    htmlFor="benefitGroupsEligibleForContribution"
                    style={labelStyle}
                  >
                    Benefit groups eligible for contribution
                  </label>
                  <PlanContributionMultiSelect
                    id="benefitGroupsEligibleForContribution"
                    name="benefitGroupsEligibleForContribution"
                    options={benefitGroupOptions}
                    value={formData.benefitGroupsEligibleForContribution}
                    onChange={handleChange}
                    disabled={benefitGroupOptions.length === 0}
                    placeholder="Select benefit group..."
                    style={controlStyle}
                  />
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="annualEEContribution" style={labelStyle}>
                    Annual contribution amount for Employee Only coverage
                  </label>
                  <input
                    type="number"
                    id="annualEEContribution"
                    name="annualEEContribution"
                    value={formData.annualEEContribution}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    style={controlStyle}
                  />
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="annualESContribution" style={labelStyle}>
                    Annual contribution amount for Employee + Spouse coverage
                  </label>
                  <input
                    type="number"
                    id="annualESContribution"
                    name="annualESContribution"
                    value={formData.annualESContribution}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    style={controlStyle}
                  />
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="annualECContribution" style={labelStyle}>
                    Annual contribution amount for Employee + Children coverage
                  </label>
                  <input
                    type="number"
                    id="annualECContribution"
                    name="annualECContribution"
                    value={formData.annualECContribution}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    style={controlStyle}
                  />
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="annualFAMContribution" style={labelStyle}>
                    Annual contribution amount for Family coverage
                  </label>
                  <input
                    type="number"
                    id="annualFAMContribution"
                    name="annualFAMContribution"
                    value={formData.annualFAMContribution}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    style={controlStyle}
                  />
                </div>

                <div style={sectionStyle}>
                  <label htmlFor="prorates" style={labelStyle}>
                    Is the contribution amount pro-rated?
                  </label>
                  <select
                    id="prorates"
                    name="prorates"
                    value={formData.prorates}
                    onChange={handleChange}
                    required
                    style={controlStyle}
                  >
                    <option value="">Select...</option>
                    {yesNoOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div style={fullWidthSectionStyle}>
              <label htmlFor="notes" style={labelStyle}>
                HSA notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                maxLength={255}
                rows={4}
                style={notesStyle}
              />
            </div>
          </div>

          <div style={buttonRowStyle}>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              style={{
                ...primaryButtonStyle,
                cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                opacity: isSubmitDisabled ? 0.6 : 1,
              }}
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployerHSAForm;
